import React, { createElement, useEffect, useRef } from "react";

import { useTranslation } from "react-i18next";

import { getModule } from "@companion-core/shared/app/Utils/config";
import { TextInShape } from "@companion-core/shared/app/Components/TextInShape";
import { useBellStore } from "@companion-core/shared/app/Store/bellStore";
import { Category } from ".";
import { trackClickBell, trackStepBell } from "@companion-core/shared/app/tracking/bell";
import { loadFormFromPath } from "@companion-core/shared/app/Utils/dynamicForm";

import "Assets/Styles/scss/bell.scss";

export const BellCategories = () => {
  const { t } = useTranslation();

  const categories = getModule("concierge").modules.bell.options.categories;
  const { setBellContent, setBellCategory, setFormPath, setHeaderAccessibilityLabel } =
    useBellStore((state) => ({
      setBellContent: state.setBellContent,
      setBellCategory: state.setBellCategory,
      setFormPath: state.setFormPath,
      setHeaderAccessibilityLabel: state.setHeaderAccessibilityLabel,
    }));

  const titleRef = useRef<HTMLHeadingElement>(null);

  const onCategoryClick = (selectedCategory: Category) => {
    setFormPath(selectedCategory.formPath);
    setBellContent("form");
    setBellCategory(selectedCategory);
    const formJson = loadFormFromPath(selectedCategory.formPath);
    if (formJson.steps[0].tracking) {
      const { name = "", chapter1 = "", chapter2 = "", chapter3 = "" } = formJson.steps[0].tracking;
      trackClickBell(name, chapter1, chapter2, chapter3);
    }
  };

  useEffect(() => {
    setHeaderAccessibilityLabel(
      t("request.concierge.header.accessibility.title.categories", {
        count: categories.length,
      })
    );
  }, [categories.length, setHeaderAccessibilityLabel, t]);

  useEffect(() => {
    trackStepBell("step_categories", "bell", "categories");
    titleRef.current?.focus({ preventScroll: true });
  }, [t]);

  const titleElement = createElement(
    "h2",
    {
      ref: titleRef,
      className: "title-8",
      tabIndex: -1,
    },
    t("requestV2.concierge.categories.title")
  );

  return (
    <div data-testid="bell__categories" className="bell__categories">
      {titleElement}
      <ul className="bell__categories-container">
        {categories.map((category: Category) => (
          <li key={`category--${category.name}`}>
            <TextInShape
              ariaLabel={t(`request.concierge.form.title.${category.name}`)}
              testID={`bell__category--${category.name}`}
              text={t(`request.concierge.form.title.${category.name}`)}
              icon={category.icon}
              value={category}
              size={"smallest"}
              onClick={() => onCategoryClick(category)}
            />
          </li>
        ))}
      </ul>
    </div>
  );
};
